import React, { useCallback, useState } from 'react';

// Utils
import { useCookies } from 'react-cookie';
import { useMutation } from 'react-query';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../../store/AppContext';
import loginSchema from '../../../validations/loginSchema';
import adminTypeOptions from '../../../constants/adminTypesOptions';

// Components
import Button from '../../atoms/Button/Button';
import AuthTemplate from '../../layout/AuthTemplate/AuthTemplate';
import InputField from '../../atoms/InputField/InputField';
import RadioGroup from '../../molecules/RadioGroup/RadioGroup';
import FlashMessage from '../../molecules/FlashMessage/FlashMessage';

// Services
import useAuthService from '../../../services/authService';

function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [{ adminType: storedAdminType }, stateDispatch] = useAppContext();
  const [, setCookie] = useCookies(['jwt']);

  const { postLoginToken } = useAuthService();
  const [postLoginError, setPostLoginError] = useState(null);

  const [adminType, setAdminType] = useState(storedAdminType || adminTypeOptions[0].name);

  const { mutate: postLoginMutate, isLoading } = useMutation(
    postLoginToken,
    {
      onSuccess: (res) => {
        if (res?.data?.access_token) {
          const expiryDate = new Date();
          expiryDate.setMonth(expiryDate.getMonth() + 1);

          setCookie(
            'jwt',
            res.data.access_token,
            {
              path: '/',
              expires: expiryDate,
              secure: true,
            },
          );
          stateDispatch({ type: 'SET_ADMIN_TYPE', payload: adminType });
          navigate('/');
        }
      },
      onError: (error) => {
        setPostLoginError(error?.response?.data?.code);
      },
    },
  );

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: (values) => {
      postLoginMutate({ adminType, ...values });
    },
    validationSchema: loginSchema,
  });

  const getInput = useCallback((name, type) => (
    <div className="m-b-15">
      <InputField
        type={type || 'text'}
        id={`login_${name}`}
        name={name}
        value={formik.values[name]}
        label={t(`login.${name}`)}
        formik={{
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
        }}
        error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        isRequired
      />
    </div>
  ), [formik]);

  return (
    <AuthTemplate title={t('login.login')}>
      {postLoginError ? (
        <FlashMessage
          message={t(`code_errors.${postLoginError}`)}
          color="error"
          className="m-b-20"
        />
      ) : null}

      <form onSubmit={formik.handleSubmit}>
        <div className="m-b-20">
          <p className="m-b-10 fs-13 fw-500">
            {`${t('login.types.admin')} :`}
          </p>
          <RadioGroup
            groupName="adminTypes"
            entries={adminTypeOptions.map((option) => ({ name: option.name, label: t(option.label) }))}
            selected={adminType}
            setSelected={(value) => setAdminType(value)}
            required
          />
        </div>

        {getInput('email')}
        {getInput('password', 'password')}

        <div className="d-flex j-end m-t-20">
          <a href="/forgot-password" className="fs-14 c-primary-500 fw-600">{t('login.forgot.link')}</a>
        </div>

        <div className="m-t-30">
          <Button
            submitType
            isLoading={isLoading}
          >
            {t('login.submit')}
          </Button>
        </div>
      </form>
    </AuthTemplate>
  );
}

export default LoginForm;
